import { getSchema, type SchemaResponse } from '@/services/schema.service'
import { useQuery } from '@tanstack/vue-query'

interface BaseOptions {
  enabled?: boolean
  filter?: 'isResource' | 'isSearchable'
}

interface OptionsWithAsOptions extends BaseOptions {
  asOptions: true
}

interface OptionsWithoutAsOptions extends BaseOptions {
  asOptions?: false
}

type SchemaOption = {
  label: string
  value: string
}

// Function overloads for different return types
export function useSchemaData(
  options: OptionsWithAsOptions,
): ReturnType<typeof useQuery<SchemaResponse, Error, SchemaOption[]>>
export function useSchemaData(
  options?: OptionsWithoutAsOptions,
): ReturnType<typeof useQuery<SchemaResponse, Error, SchemaResponse>>
export function useSchemaData(
  options: BaseOptions & { asOptions?: boolean },
): ReturnType<typeof useQuery<SchemaResponse, Error, SchemaResponse | SchemaOption[]>>
export function useSchemaData(options: BaseOptions & { asOptions?: boolean } = {}) {
  return useQuery({
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryKey: ['schema'],
    queryFn: getSchema,
    ...options,
    select: (data: SchemaResponse) => {
      let filteredData = data.Data

      // Apply filter if specified
      if (options.filter) {
        filteredData = data.Data.filter((item) => {
          if (options.filter === 'isResource' && item.IsResource) return true

          if (options.filter === 'isSearchable' && item.IsSearchable) return true

          return false
        })
      }

      // Create the filtered response
      const filteredResponse: SchemaResponse = {
        Data: filteredData,
        Count: filteredData.length,
      }

      if (options.asOptions) {
        return filteredResponse.Data.map((item) => ({
          label: item.Name,
          value: item.Index,
        }))
      }

      return filteredResponse
    },
  })
}
